import { IAirtableAttachment } from "@rogoag/airtable/rogo.at";
import { Company, Job, Grower, Contact, JobStatusNumber } from "../../../types/dataModels";
import { Filter, StandardAxiosConfig, getBaseDataURL } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { API, Branch } from "../../..";
import axios from "axios";
import { JobFlags } from "../../../../utilities/types";
import { GeoData, LogData } from "@rogoag/shared-ts";
import * as turf from "@turf/turf";
import { Feature, Point } from "geojson";
import { CREATE_NEW_SALESPERSON } from "../../../../utilities/constants";
import { Oops } from "../../../../utilities/oops";
import { logger } from "../../../../utilities/logger";

/**
 * Process items in batches to limit concurrency
 * @param items Array of items to process
 * @param batchSize Number of items to process concurrently (default: 10)
 * @param processFn Function to process each item
 * @param onBatchComplete Optional callback after each batch completes
 * @returns Array of results
 */
async function processBatch<T, R>(
	items: T[],
	batchSize: number = 10,
	processFn: (item: T) => Promise<R>,
): Promise<R[]> {
	const results: R[] = [];
	for (let i = 0; i < items.length; i += batchSize) {
		const batch = items.slice(i, i + batchSize);
		const batchResults = await Promise.all(batch.map(processFn));
		results.push(...batchResults);
	}
	return results;
}

class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	public async many(jobs: Partial<Job>[], source?: JobFlags): Promise<Job[]> {
		const user = await API.Authentication.getCurrentUser();
		const contact = await API.Data.Contacts.get.byId(user.id);
		const uniqueDealIds = Array.from(new Set(jobs.filter((job) => job.dealIds?.[0]).map((job) => job.dealIds![0])));
		const deals = uniqueDealIds.length > 0 ? await API.Data.Deals.get.byIds(uniqueDealIds) : [];
		const logData: LogData = {
			function: "API.Data.Jobs.create.many",
			jobs_count: jobs.length,
			source: source,
			user: user.email,
		};

		await associateExternalGrowers();
		await addNewSalespersons();
		await associateSalespersonsAndBranches();
		const jobsToCreate = await prepareJobsForCreation();
		await logGeoDataToSentry();

		logData.function = "API.Data.Jobs.create.many.creatingJobs";
		logger.log("Creating jobs", logData);
		const createdJobs = await super.many(jobsToCreate);
		const updatedJobs = await uploadAttachments();
		if (!updatedJobs) throw new Error("Job submission failed");

		return updatedJobs;

		async function logGeoDataToSentry() {
			logData.function = "API.Data.Jobs.create.many.logGeoDataToSentry";

			// A temporary logging idea unitl I can think of something better.
			// Very rarely, customers are submitting jobs that don't have geodata,
			// and it seems that the data is just getting lost somewhere in the process,
			// for a variety of un-related reasons.
			// Hopefully this will make it possible to grab their data without asking them.
			for (const job of jobs) {
				logData.temp_id = job.rogoAppVersion;

				if (job.files?.boundaryGeoJSON) {
					logger.log(`Boundary GeoJson for ${job.rogoAppVersion}`, { ...logData, boundary: job.files.boundaryGeoJSON });
				}

				if (job.files?.pointsGeoJSON) {
					logger.log(`Points GeoJson for ${job.rogoAppVersion}`, { ...logData, points: job.files.pointsGeoJSON });
				}

				if (job.files?.zonesGeoJSON) {
					logger.log(`Zones GeoJson for ${job.rogoAppVersion}`, { ...logData, zones: job.files.zonesGeoJSON });
				}
			}
		}

		async function associateSalespersonsAndBranches() {
			let contactsToUpdate: Contact[] = [];
			let branchesToUpdate: Branch[] = [];
			for (const job of jobs) {
				if (job.salespersonIds && job.salespersonIds.length > 0 && job.branchIds && job.branchIds.length > 0) {
					const contacts = await API.Data.Contacts.get.byIds(job.salespersonIds);
					const branches = await API.Data.Branches.get.byIds(job.branchIds);
					for (const contact of contacts) {
						contactsToUpdate.push({
							...contact,
							salespersonBranchIds: Array.from(new Set([...(contact.salespersonBranchIds || []), ...job.branchIds])),
							isSalesperson: true,
						});
					}
					for (const branch of branches) {
						branchesToUpdate.push({
							...branch,
							salespersonIds: Array.from(new Set([...(branch.salespersonIds || []), ...job.salespersonIds])),
						});
					}
				}
			}

			contactsToUpdate = contactsToUpdate.filter(
				(contact, index, self) => contact.id && self.findIndex((c) => c.id === contact.id) === index,
			);
			branchesToUpdate = branchesToUpdate.filter(
				(branch, index, self) => branch.id && self.findIndex((b) => b.id === branch.id) === index,
			);

			if (contactsToUpdate.length == 0 && branchesToUpdate.length == 0) return;
			logData.function = "API.Data.Jobs.create.many.associateSalespersonsAndBranches";
			logger.log("Associating salespersons and branches", logData);

			await API.Data.Contacts.update.many(contactsToUpdate);
			await API.Data.Branches.update.many(branchesToUpdate);
		}

		async function addNewSalespersons() {
			const newSalespersons = jobs
				.map((job) => job.newSalesperson)
				.filter((sp) => !!sp)
				.filter((sp, idx, arr) =>
					sp?.name
						? arr.findIndex(
								(s) =>
									s?.name?.trim().toLowerCase() === sp.name.trim().toLowerCase() &&
									s?.phone?.trim() === sp.phone?.trim(),
							) === idx
						: true,
				);
			if (newSalespersons.length == 0) return;

			const newContacts: Partial<Contact>[] = [];
			for (const newSalesperson of newSalespersons) {
				if (!newSalesperson.name || newSalesperson.name.trim() === "") continue;

				const firstName = newSalesperson.name.split(" ").at(0) || "";
				const lastName = newSalesperson.name.split(" ").slice(1).join(" ") || "";

				const newContact: Partial<Contact> = {
					firstName: firstName,
					lastName: lastName,
					mobilePhone: newSalesperson.phone,
					companyIds: contact.companyIds,
					isSalesperson: true,
				};
				newContacts.push(newContact);
			}

			if (newContacts.length == 0) return;

			logData.function = "API.Data.Jobs.create.many.addNewSalespersons";
			logger.log("Adding new salespersons", logData);
			const contacts = await API.Data.Contacts.create.many(newContacts);

			for (const job of jobs) {
				job.salespersonIds = job.salespersonIds?.filter((id) => id !== CREATE_NEW_SALESPERSON);

				const newSalesperson = job.newSalesperson;
				if (!newSalesperson) continue;

				const salesperson = contacts.find(
					(c) => c.name.trim() === newSalesperson.name.trim() && c.mobilePhone.trim() === newSalesperson.phone.trim(),
				);
				if (!job.salespersonIds) job.salespersonIds = [];
				if (salesperson) job.salespersonIds.push(salesperson.id);
			}
		}

		async function associateExternalGrowers() {
			const externalGrowers = jobs.map((job) => job.externalGrower).filter((g) => !!g);
			let existingGrowers: Grower[] = [];
			if (externalGrowers.length > 0) {
				existingGrowers = await API.Data.Growers.get.byFilter({
					type: "OR",
					fields: externalGrowers.map((g) => ({ externalId: g.id })),
				} as Filter<Grower>);
			}
			const newExternalGrowers = externalGrowers.filter((g) => !existingGrowers.some((eg) => eg.externalId === g.id));
			const companyId = contact.companyIds.at(0);
			const companyIds = companyId ? [companyId] : [];
			const newGrowers: Partial<Grower>[] = newExternalGrowers.map((g) => ({
				name: g.name,
				companyIds: companyIds,
				externalId: g.id,
			}));
			if (newGrowers.length == 0) return;

			logData.function = "API.Data.Jobs.create.many.associateExternalGrowers";
			logger.log("Associating external growers", logData);
			const createdGrowers = await API.Data.Growers.create.many(newGrowers);
			const allGrowers = [...existingGrowers, ...createdGrowers];
			for (const job of jobs) {
				const grower = allGrowers.find((g) => g.externalId === job.externalGrower?.id);
				if (grower) job.growerIds = [grower.id];
			}
		}

		async function prepareJobsForCreation() {
			return await Promise.all(
				jobs.map(async (job) => {
					// This is a hack to ensure that we can match the job after creation,
					// in order to upload the files into the appropriate S3 bucket
					job.rogoAppVersion = `temp_${crypto.randomUUID()}`;
					job.boundaryChangeType = job.collectBoundary ? "Major" : undefined;
					job.isPointsAttached =
						job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0 ? true : false;

					job.jobFlags = job.jobFlags ? [...job.jobFlags] : [];
					if (source) job.jobFlags.push(source);
					if (source == "Created Portal API Agrian") job.jobFlags.push("Agrian");
					if (contact.isSuperUser) job.jobFlags.push("ROGO Created");

					await handleZonesForCompositeJobs(job);

					const hasZones = job.files?.zonesGeoJSON && job.files.zonesGeoJSON.features?.length > 0;
					const hasPoints = job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0;

					if (hasZones && hasPoints) {
						logData.function = "API.Data.Jobs.create.many.prepareJobsForCreation";
						logger.log("Renumbering zone cores", { ...logData, temp_id: job.rogoAppVersion });
						job.files!.pointsGeoJSON = await renumberZoneCores(job.files!.zonesGeoJSON!, job.files!.pointsGeoJSON!);
					}

					return job;
				}),
			);
		}

		async function handleZonesForCompositeJobs(job: Partial<Job>) {
			if (job.dealIds && job.dealIds.length > 0) {
				if (job.files) {
					if (!job.dealIds) return;
					const deal = deals.find((d) => d.id === job.dealIds![0]);
					if (!deal) return;
					if (deal.isComposite && job.files.boundaryGeoJSON) {
						logData.function = "API.Data.Jobs.create.many.handleZonesForCompositeJobs";
						logger.log("Handling zones for composite job", { ...logData, temp_id: job.rogoAppVersion });

						// In Composite jobs, the zone is the same as the boundary,
						// so we're doing the copy-and-paste here to save time in map-making
						// Deep copy to avoid mutating the original boundary
						const znsGeoJson = JSON.parse(JSON.stringify(job.files.boundaryGeoJSON));
						if (znsGeoJson.features) {
							znsGeoJson.features.forEach((feature: any) => {
								if (!feature.properties) feature.properties = {};
								feature.properties.ZoneID = 1;
							});
						}
						// Only set GeoJSON - SHP will be generated at upload time
						job.files.zonesGeoJSON = znsGeoJson;
					} else if (deal.isMultiComposite) {
						// In MultiComposite jobs, we still don't want their zones,
						// but we can't simply copy-and-paste the boundary
						job.files.zonesGeoJSON = undefined;
					}
				}
			}
		}

		/**
		 * We can't trust the core number coming from customers or external software,
		 * and they generally don't really care what the numbers are anyway,
		 * so here we ensure that the core numbers are unique, consistent, and match the zone they belong to.
		 */
		async function renumberZoneCores(
			znsGeoJson: GeoJSON.FeatureCollection,
			ptsGeoJson: GeoJSON.FeatureCollection,
		): Promise<GeoJSON.FeatureCollection> {
			// Deep copy to avoid mutating the original
			const resultGeoJson = JSON.parse(JSON.stringify(ptsGeoJson)) as GeoJSON.FeatureCollection;

			for (const zone of znsGeoJson.features) {
				let coreNumber = 0;
				for (const point of resultGeoJson.features) {
					const pointGeom = point.geometry as Point;

					if (turf.booleanPointInPolygon(pointGeom.coordinates, zone as Feature<any>)) {
						if (zone.properties && zone.properties.ZoneID) {
							coreNumber++;
							point.properties = point.properties || {};
							point.properties.CoreID = `${zone.properties.ZoneID}.${coreNumber}`;
						}
					}
				}
			}

			return resultGeoJson;
		}

		async function uploadAttachments() {
			// The files are uploaded after job creation because we need the job id in order to upload the files into the appropriate "id" folder in S3
			// The job is then updated with the attachment URLs

			try {
				const jobsToUpdate = (
					await processBatch(
						jobs,
						10, // Process 10 jobs at a time
						async (job) => {
							const matchingJob = createdJobs.find((id) => id.rogoAppVersion === job.rogoAppVersion);

							if (matchingJob) {
								logData.function = "API.Data.Jobs.create.many.uploadAttachments";
								logData.temp_id = job.rogoAppVersion;
								logData.airtable_record_id = job.id;
								logData.airtable_table = "Jobs";
								logger.log("Uploading attachments for job", logData);

								const boundarySHPFiles = job.files?.boundaryGeoJSON
									? [await GeoData.toSHP_File(job.files.boundaryGeoJSON, "GeoJSON", "boundary")]
									: [];
								const pointsSHPFiles = job.files?.pointsGeoJSON
									? [await GeoData.toSHP_File(job.files.pointsGeoJSON, "GeoJSON", "points")]
									: [];
								const zonesSHPFiles = job.files?.zonesGeoJSON
									? [await GeoData.toSHP_File(job.files.zonesGeoJSON, "GeoJSON", "zones")]
									: [];

								const boundary =
									boundarySHPFiles.length > 0 ? await API.Data.Jobs.update.files(matchingJob.id, boundarySHPFiles) : [];
								const points =
									pointsSHPFiles.length > 0 ? await API.Data.Jobs.update.files(matchingJob.id, pointsSHPFiles) : [];
								const zones =
									zonesSHPFiles.length > 0 ? await API.Data.Jobs.update.files(matchingJob.id, zonesSHPFiles) : [];
								const fieldNotes = job.files?.fieldNotes
									? await API.Data.Jobs.update.files(matchingJob.id, job.files.fieldNotes)
									: [];

								matchingJob.rogoAppVersion = undefined;
								matchingJob.attachments = {
									boundarySHP: boundary,
									pointsSHP: points,
									zonesSHP: zones,
									fieldNotes: fieldNotes,
								};

								const jobUpdate: Job = {
									...matchingJob,
									rogoAppVersion: "",
									attachments: {
										boundarySHP: boundary,
										pointsSHP: points,
										zonesSHP: zones,
										fieldNotes: fieldNotes,
									},
								};

								return jobUpdate;
							} else {
								throw new Error(`Matching job not found for ${job.rogoAppVersion}. Files were not uploaded.`);
							}
						},
					)
				).filter((job) => !!job);

				if (jobsToUpdate.length === 0) return;
				logData.function = "API.Data.Jobs.create.many.uploadAttachments.updatingJobs";
				logData.airtable_record_id = createdJobs.map((job) => job.id).join(",");
				logger.log("Updating jobs with attachment URLs", logData);
				const updatedJobs = await API.Data.Jobs.update.many(jobsToUpdate);
				return updatedJobs;
			} catch (error) {
				Oops.handleError(error, "jobs.create.many.uploadAttachments");
			}
		}
	}

	async one(
		job: Partial<Job>,
		source?: "Created Portal Single" | "Created Portal Bulk" | "Created Portal API Proagrica",
	): Promise<Job> {
		return (await this.many([job], source))[0];
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", true);
	}

	public async byCompany(company: Company) {
		if (!company.name) return [];
		const filter: Filter<Job> = { fields: { companyName: company.name } };
		const jobs = await super.byFilter(filter);
		return jobs;
	}

	/**
	 * Retrieves jobs associated with the specified growers.
	 *
	 * @param growers - An array of `Grower` objects to filter jobs by. If empty or undefined, returns an empty array.
	 * @param isUnreadyOnly - Optional. If `true`, filters jobs to only those that are unready (`statusNumber` is `0`). Defaults to `false`.
	 */
	public async byGrowers(growers: Grower[], isUnreadyOnly: boolean = false): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const growerFilter: Filter<Job> = {
			type: "OR",
			fields: [
				// We have to search both, because Grower names are not normalized
				...growers.map((g) => ({ growerIds: [g.name] })),
				...growers.map((g) => ({ growerName: g.name })),
			],
		};
		const unreadyFilter: Filter<Job> = {
			type: "AND",
			fields: [{ statusNumber: JobStatusNumber.Unready }, growerFilter],
		};
		const jobs = await super.byFilter(isUnreadyOnly ? unreadyFilter : growerFilter);
		return jobs;
	}

	public async lastCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop`;
		const response = await axios.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop`;
		const response = await axios.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}
export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
	}
}
